import React, { useEffect, useMemo, useState } from 'react';
import './StarfieldBackground.css';

/**
 * Site-wide starry background. A single fixed layer behind all content
 * (z-index:-1, pointer-events:none). Stars twinkle in place — they do NOT
 * follow the cursor (unlike the login page's parallax starfield).
 *
 * NOT RENDERED ON PHONES. CSS alone (`display:none`) would still build 160 DOM
 * nodes and hand the compositor 160 animated layers to decide about; returning
 * null means the work never exists. This layer was the main reason phones ran
 * hot: 160 glowing twinkling dots plus two 480px circles with a 90px blur
 * animating transform AND scale — a blur that cannot be cached, re-rasterised
 * every frame, forever, on every route. Worse, it sat BEHIND ~40 backdrop-filter
 * surfaces, so each twinkle invalidated every frosted card on screen.
 *
 * Skipped when: the viewport is phone-sized, the user asked for reduced motion,
 * or the browser reports data-saver (a decent proxy for "cheap device / metered
 * connection"). Desktop is unchanged.
 */

const MOBILE_QUERY = '(max-width: 768px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function decorationsUnwanted(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  if (window.matchMedia(MOBILE_QUERY).matches) return true;
  if (window.matchMedia(REDUCED_MOTION_QUERY).matches) return true;
  const conn = (navigator as any)?.connection;
  return !!conn?.saveData;
}

const StarfieldBackground: React.FC = () => {
  const [skip, setSkip] = useState<boolean>(decorationsUnwanted);
  /**
   * Pause while the tab is hidden. CSS animations keep ticking in a backgrounded
   * tab on several mobile browsers, which is heat spent on something nobody can
   * see — and this app is usually left open in a tab all day.
   */
  const [hidden, setHidden] = useState<boolean>(
    typeof document !== 'undefined' ? document.hidden : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mobile = window.matchMedia(MOBILE_QUERY);
    const motion = window.matchMedia(REDUCED_MOTION_QUERY);
    const sync = () => setSkip(decorationsUnwanted());
    // Rotating a phone or resizing a desktop window must flip this either way.
    mobile.addEventListener('change', sync);
    motion.addEventListener('change', sync);
    const onVis = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      mobile.removeEventListener('change', sync);
      motion.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const stars = useMemo(() => {
    if (skip) return [];
    const rnd = (a: number, b: number) => a + Math.random() * (b - a);
    return Array.from({ length: 160 }, (_, i) => ({
      id: i,
      top: rnd(0, 100),
      left: rnd(0, 100),
      size: rnd(1.4, 3.6),
      o: rnd(0.55, 1),
      dur: rnd(2.5, 6.5),
      delay: rnd(0, 7),
      gold: Math.random() < 0.3,
    }));
  }, [skip]);

  if (skip || hidden) return null;

  return (
    <div className="app-starfield" aria-hidden="true">
      <span className="app-neb app-neb-1" />
      <span className="app-neb app-neb-2" />
      {stars.map((s) => (
        <span
          key={s.id}
          className={`app-star${s.gold ? ' gold' : ''}`}
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            animationDuration: `${s.dur}s`,
            animationDelay: `${s.delay}s`,
            ['--o' as any]: s.o,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
};

export default StarfieldBackground;
